import { useForm } from "react-hook-form";
import { useUpdateResidentOtherInformationMutation } from "app/services/api/residentsApi";
import { useGetOtherInformationCategoriesQuery } from "app/services/api/categoriesApi";
import { handleBackendError } from "ui/utils/Errors";
import { useDisplayToast } from "ui/hooks/useDisplayToast";
import { useResidentContext } from "../provider/ResidentProvider";
import {
  OtherInformationForm,
  OtherInformationFormValue,
} from "app/services/api/types/residents/models/AddResidentOtherInformationForm";
import { OtherInformationResponse } from "app/services/api/types/residents/responses/OtherInformationCategoriesResponse";
import {
  UpdateOtherInformationCategory,
  UpdateOtherInformationRequest,
} from "app/services/api/types/residents/requests/UpdateOtherInformationRequest";
import { isNullOrUndefined } from "ui/utils/Validators";
import { ResidentModel } from "app/services/api/types/residents/models/ResidentModel";

const generateDefaults = (
  categories: OtherInformationResponse | undefined,
  resident?: ResidentModel,
) => {
  if (!categories || !resident) return {};

  const defaults = categories.reduce(
    (previous, current) => {
      const selectedCategory = resident.other_information.find(
        (item) =>
          item.other_information_category.id === current.id ||
          item.other_information_category.parent_id === current.id,
      );

      const selectedId = selectedCategory?.other_information_category.id ?? 0;

      const matchedChild = current.children?.find(
        (child) => child.id === selectedId,
      );
      const selectedDefinition = matchedChild || current;

      if (selectedDefinition.requires_description) {
        previous[current.id] = {
          value: selectedId,
          description: selectedCategory?.description ?? "",
        };
      } else {
        previous[current.id] = {
          value: selectedId,
        };
      }

      return previous;
    },
    {} as Record<number, OtherInformationFormValue>,
  );

  return defaults;
};

export const useHandleResidentOtherInformation = () => {
  const { showNewToast, showErrorToast } = useDisplayToast();
  const { resident } = useResidentContext();

  const [triggerUpdateResident] = useUpdateResidentOtherInformationMutation();
  const { data: categories } = useGetOtherInformationCategoriesQuery();

  const { control, setError, handleSubmit, setValue } =
    useForm<OtherInformationForm>({
      defaultValues: generateDefaults(categories, resident),
    });

  const createPayload = (
    formValues: OtherInformationForm,
  ): UpdateOtherInformationRequest => {
    const otherInformation = Object.entries<OtherInformationFormValue>(
      formValues,
    ).reduce<UpdateOtherInformationCategory[]>(
      (previous, [parentCategoryId, item]) => {
        if (isNullOrUndefined(item.value) || item.value === 0) return previous;

        previous?.push({
          other_information_category_id: Number(item.value),
          description: item.description,
        });

        return previous;
      },
      [],
    );

    return { other_information: otherInformation };
  };

  const submitForm = () => {
    return new Promise<boolean>((resolve) => {
      handleSubmit(
        async (formValues) => {
          try {
            const payload = createPayload(formValues);

            await triggerUpdateResident({
              residentId: resident?.id!,
              body: payload,
            }).unwrap();

            showNewToast({
              title: "Resident Other Information Updated",
              message: "Successfully updated resident's other information",
              type: "success",
            });

            resolve(true);
          } catch (error) {
            handleBackendError(error, setError);
            showErrorToast(error);
            resolve(false);
          }
        },
        (errors) => {
          console.error("Form validation errors:", errors);
          resolve(false);
        },
      )();
    });
  };

  return { control, submitForm, categories, setValue };
};
